import React, { useRef, useState } from 'react';
import { Box, Button, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { PinnacleTextField } from '../../../../../components/PinnacleTextField';
import { handleApiError } from '../../../../../utils/errorHandler';
import { pushNotification } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { Program } from '../../../../../api/interfaces/Program';
import { ReferenceFormRequest } from '../../../../../api/interfaces/Reference';
import { FormData } from '../../../hooks/useReferenceForm';
import { tinyEditorApiKey } from '../../../../../utils/constants';
import { Editor } from '@tinymce/tinymce-react';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { uploadFile } from '../../../../../api/services/file';

interface ContentManagementProps {
    programs: Program[];
    touchedFields: Record<keyof FormData, boolean>;
    onBlur: (field: keyof FormData) => void;
    isSubmitting: boolean;
    formData: ReferenceFormRequest;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    // onSuccess: (updatedUser: User, skipClose?: boolean) => void;
    disabled?: boolean;
    // setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;

}

const ContentManagement: React.FC<ContentManagementProps> = ({
    programs,
    touchedFields,
    onBlur,
    isSubmitting,
    formData,
    onChange,
    disabled
}) => {
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [fileContentImageUrl, setFileContentImageUrl] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);
    const handleEditorChange = (content: string) => {
        const syntheticEvent = {
            target: {
                name: 'content',
                value: content,
            }
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
    };


    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedImage(file);
        setIsUploading(true);
        setFileContentImageUrl('');

        try {
            const uploadResponse = await uploadFile('references', file);
            setFileContentImageUrl(uploadResponse.data.location);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Editor
                value={formData.content}
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY || tinyEditorApiKey}
                init={{
                    height: 500,
                    menubar: 'edit view insert format tools table help',
                    plugins: [
                        'advlist autolink lists link image charmap print preview anchor',
                        'searchreplace visualblocks code fullscreen',
                        'insertdatetime media table paste code help wordcount',
                    ],
                    toolbar:
                        'undo redo | formatselect | bold italic underline backcolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'bullist numlist outdent indent | removeformat | image | help',
                    toolbar_mode: 'sliding',
                    branding: false,
                    paste_data_images: true,
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                onEditorChange={handleEditorChange}
                onBlur={() => onBlur('content')}
            />
            {touchedFields.content && !formData.content.trim() && (
                <Box color="error.main" mt={1} fontSize={13}>
                    Content is required
                </Box>
            )}

            <Stack spacing={2} sx={{ mb: 2 }}>
                <Box
                    onClick={handleUploadClick}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                        <Box>
                            {isUploading ? (
                                <>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Uploading...
                                    </Typography>
                                    <LinearProgress sx={{ mt: 1, width: '100%' }} />
                                </>
                            ) : fileContentImageUrl ? (
                                <>
                                    <Typography variant="subtitle1" fontWeight={500} noWrap sx={{
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        maxWidth: 200
                                    }}>
                                        {selectedImage?.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Upload complete — click to change image
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Upload File Content Image
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Browse file to upload
                                    </Typography>
                                </>
                            )}
                        </Box>
                    </Stack>

                    <input
                        type="file"
                        accept="image/jpeg, image/jpg, image/png, image/svg+xml"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="flex-start">
                <PinnacleTextField
                    fullWidth
                    variant="outlined"
                    value={fileContentImageUrl}
                    disabled
                />

                <Tooltip title="Copy">
                    <Button
                        variant="outlined"
                        onClick={() => navigator.clipboard.writeText(fileContentImageUrl)}
                        sx={{
                            height: '56px',
                            borderRadius: '4px',
                            color: '#A8A8A8',
                            borderColor: '#A8A8A8',
                            textTransform: 'none',
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        disabled={!fileContentImageUrl || isUploading}
                    >
                        <ContentCopyOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                        Copy
                    </Button>
                </Tooltip>
            </Stack>
        </Box>
    );
};


export default ContentManagement;
